// Dishes API Types
export interface DishIngredientInput {
  foodId?: string | null;
  customFoodId?: string | null;
  servingSizeId?: string | null;
  gramsEquivalent?: number | null;
  quantity?: number | null;
  preparationNote?: string | null;
  sortOrder?: number;
}

export interface DishPreparationStepsInputDto {
  stepOrder: number;
  stepContent: string;
  stepContentEn?: string | null;
  estimatedTimeMinutes?: number | null;
  isRequired?: boolean;
  stepType?: string | null;
}

export interface CreateDishDto {
  name: string;
  nameEn?: string | null;
  slug: string;
  description?: string | null;
  descriptionEn?: string | null;
  prepTimeMinutes?: number | null;
  cookTimeMinutes?: number | null;
  difficultyLevel: number;
  cookingTips?: string | null;
  imageUrl?: string | null;
  videoUrl?: string | null;
  isPublic: boolean;
  dishIngredients?: DishIngredientInput[];
  dishPreparationSteps?: DishPreparationStepsInputDto[];
}

export interface UpdateDishDto {
  name?: string;
  nameEn?: string | null;
  slug?: string;
  description?: string | null;
  descriptionEn?: string | null;
  prepTimeMinutes?: number | null;
  cookTimeMinutes?: number | null;
  difficultyLevel?: number;
  cookingTips?: string | null;
  imageUrl?: string | null;
  videoUrl?: string | null;
  isPublic?: boolean;
  isVerified?: boolean;
  popularityScore?: number;
  isActive?: boolean;
  dishIngredients?: DishIngredientInput[];
  dishPreparationSteps?: DishPreparationStepsInputDto[];
}

export interface DishIngredientDto {
  id: string;
  foodId?: string;
  customFoodId?: string;
  servingSizeId?: string;
  quantity?: number;
  gramsEquivalent?: number;
  preparationNote?: string;
  sortOrder?: number;
}

export interface DishPreparationStepDto {
  id: string;
  createdAt: string;
  updatedAt: string;
  dishId: string;
  stepOrder: number;
  stepContent: string;
  stepContentEn?: string | null;
  estimatedTimeMinutes: number;
  isRequired: boolean;
  stepType?: string | null;
}

export interface DishDto {
  id: string;
  createdByUserId: string;
  name: string;
  nameEn?: string | null;
  slug: string;
  exportedFoodId?: string | null;
  description?: string | null;
  descriptionEn?: string | null;
  prepTimeMinutes?: number | null;
  cookTimeMinutes?: number | null;
  difficultyLevel: number;
  servingsCount: number;
  energyKcal?: number | null;
  proteinG?: number | null;
  carbohydratesG?: number | null;
  fatTotalG?: number | null;
  totalWeightGrams?: number | null;
  instructions?: string | null;
  instructionsEn?: string | null;
  cookingTips?: string | null;
  imageUrl?: string | null;
  videoUrl?: string | null;
  isPublic: boolean;
  isVerified: boolean;
  popularityScore: number;
  isActive: boolean;
  ingredients?: DishIngredientDto[];
  preparationSteps?: DishPreparationStepDto[];
  createdAt: string;
  updatedAt: string;
}

export interface DishesListParams {
  page?: number;
  take?: number;
  order?: "ASC" | "DESC";
  sortBy?: "name" | "createdAt" | "updatedAt";
  createdByUserId?: string;
  isPublic?: boolean;
  isVerified?: boolean;
  isActive?: boolean;
  search?: string;
}

export interface DishesListResponse {
  data: DishDto[];
  meta: {
    page: number;
    take: number;
    itemCount: number;
    pageCount: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
}

export interface ExportDishToFoodResponseDto {
  foodId: string;
  dishId: string;
  foodName: string;
  slug: string;
  message: string;
}

export interface MyDishesParams {
  page?: number;
  take?: number;
  order?: "ASC" | "DESC";
}

// Request Parameters for GET /dishes/admin/custom-dishes
export interface GetCustomDishesParams {
  page?: number; // default: 1, minimum: 1
  take?: number; // default: 10, minimum: 1, maximum: 50
  order?: "ASC" | "DESC"; // default: "ASC"
  userId?: string; // UUID - Filter by user ID (Admin only)
  sortBy?: "name" | "createdAt" | "updatedAt"; // default: "createdAt"
}

// Filter options for UI
export interface DishFilterOptions {
  dishCategories: Array<{ value: string; label: string }>;
  cuisineTypes: Array<{ value: string; label: string }>;
  mealTypes: Array<{ value: string; label: string }>;
  difficultyLevels: Array<{ value: number; label: string }>;
}

// Constants
export const DIFFICULTY_LEVELS = [
  { value: 1, label: "Rất dễ" },
  { value: 2, label: "Dễ" },
  { value: 3, label: "Trung bình" },
  { value: 4, label: "Khó" },
  { value: 5, label: "Rất khó" },
];

export const MEAL_TYPES = [
  { value: "Breakfast", label: "Bữa sáng" },
  { value: "Lunch", label: "Bữa trưa" },
  { value: "Dinner", label: "Bữa tối" },
  { value: "Snack", label: "Đồ ăn nhẹ" },
  { value: "Dessert", label: "Tráng miệng" },
];

export const CUISINE_TYPES = [
  { value: "Vietnamese", label: "Việt Nam" },
  { value: "Chinese", label: "Trung Quốc" },
  { value: "Japanese", label: "Nhật Bản" },
  { value: "Korean", label: "Hàn Quốc" },
  { value: "Thai", label: "Thái Lan" },
  { value: "Italian", label: "Ý" },
  { value: "French", label: "Pháp" },
  { value: "American", label: "Mỹ" },
  { value: "Mexican", label: "Mexico" },
  { value: "Indian", label: "Ấn Độ" },
  { value: "Other", label: "Khác" },
];

export interface Micronutrients {
  fiberG?: number | null;
  sugarG?: number | null;
  sugarsAddedG?: number | null;
  sodiumMg?: number | null;
  vitaminAMcg?: number | null;
  vitaminCMg?: number | null;
  vitaminDMcg?: number | null;
  vitaminD3Mcg?: number | null;
  vitaminEMg?: number | null;
  vitaminKMcg?: number | null;
  vitaminB1Mg?: number | null;
  vitaminB2Mg?: number | null;
  vitaminB3Mg?: number | null;
  vitaminB5Mg?: number | null;
  vitaminB6Mg?: number | null;
  folateMcg?: number | null;
  vitaminB12Mcg?: number | null;
  vitaminB7Mcg?: number | null;
  calciumMg?: number | null;
  ironMg?: number | null;
  magnesiumMg?: number | null;
  phosphorusMg?: number | null;
  potassiumMg?: number | null;
  zincMg?: number | null;
  copperMcg?: number | null;
  chromiumMcg?: number | null;
  fluorideMg?: number | null;
  cholesterolMg?: number | null;
  saturatedFatG?: number | null;
  transFatG?: number | null;
  omega3TotalG?: number | null;
  omega6TotalG?: number | null;
  omega3LinolenicG?: number | null;
  omega6LinoleicG?: number | null;
  alcoholG?: number | null;
  caffeineMg?: number | null;
  polyphenolsMg?: number | null;
  cholineMg?: number | null;
  iodineMcg?: number | null;
  seleniumMcg?: number | null;
  manganeseMg?: number | null;
  chlorineMg?: number | null;
  histidineMg?: number | null;
  isoleucineMg?: number | null;
  leucineMg?: number | null;
  lysineMg?: number | null;
  methionineMg?: number | null;
  cysteinMg?: number | null;
  phenylalanineMg?: number | null;
  threonineMg?: number | null;
  tryptophanMg?: number | null;
  valineMg?: number | null;
  retinolMcg?: number | null;
  betaCaroteneMcg?: number | null;
  alphaCaroteneMcg?: number | null;
  betaCryptoxanthinMcg?: number | null;
  luteinZeaxanthinMcg?: number | null;
  lycopeneMcg?: number | null;
  vitaminD2Mcg?: number | null;
  vitaminD4Mcg?: number | null;
  vitaminEAlphaMg?: number | null;
  vitaminKPhylloquinoneMcg?: number | null;
  vitaminKMenaquinone4Mcg?: number | null;
  vitaminKDihydrophylloquinoneMcg?: number | null;
  folateFoodMcg?: number | null;
  folicAcidMcg?: number | null;
  fiberSolubleG?: number | null;
  fiberInsolubleG?: number | null;
  fattyAcidsPolyunsaturatedG?: number | null;
  fattyAcidsMonounsaturatedG?: number | null;
  fatPlantG?: number | null;
  fatAnimalG?: number | null;
  omega9OleicG?: number | null;
  epaG?: number | null;
  dpaG?: number | null;
  dhaG?: number | null;
  palmiticG?: number | null;
  margaricG?: number | null;
  stearicG?: number | null;
  arachidicG?: number | null;
  behenicG?: number | null;
  lignocericG?: number | null;
  myristoleicG?: number | null;
  palmitoleicG?: number | null;
  arachidonicG?: number | null;
  arginineMg?: number | null;
  tyrosineMg?: number | null;
  alanineMg?: number | null;
  asparticAcidMg?: number | null;
  glutamicAcidMg?: number | null;
  glycineMg?: number | null;
  prolineMg?: number | null;
  serineMg?: number | null;
  starchG?: number | null;
  lactoseG?: number | null;
  glucoseG?: number | null;
  fructoseG?: number | null;
  galactoseG?: number | null;
  maltoseG?: number | null;
  sucroseG?: number | null;
  flavonoidsMg?: number | null;
  phenolicAcidsMg?: number | null;
  inulinG?: number | null;
  betaGlucansG?: number | null;
  glutathioneMg?: number | null;
  anthocyanidinsMg?: number | null;
  egcgMg?: number | null;
  totalIsoflavoneMg?: number | null;
  daidzeinMg?: number | null;
  genisteinMg?: number | null;
  glycetinMg?: number | null;
  phytosterolMg?: number | null;
  purineMg?: number | null;
  ashG?: number | null;
  waterG?: number | null;
}

export interface DishMicronutrientsResponse {
  dishId: string;
  servingsCount: number;
  gramsPerServing: number;
  micronutrients: Micronutrients;
}

// Toggle Dish Active Status DTO
export interface ToggleDishActiveDto {
  adminNote?: string | null;
}
